/**
 * Game Scene
 *
 * Main gameplay scene - manages game loop, entities, and systems.
 * Phase 1: Core jump mechanic with Blobo and test platforms.
 * Phase 2: Tilt controls with world rotation and gravity rotation.
 */

import Phaser from 'phaser';
import { Blobo } from '../entities/Blobo';
import { Platform } from '../entities/Platform';
import { Collectible, CollectibleType } from '../entities/Collectible';
import { GAME_CONSTANTS, SurfaceType, GAME_EVENTS } from '../config/constants';
import { TYPOGRAPHY } from '../config/designSystem';
import { TiltSystem } from '../systems/TiltSystem';
import { SoundManager } from '../systems/SoundManager';
import { HapticManager } from '../systems/HapticManager';
import { DevTools } from '../systems/DevTools';
import { BoundaryVisualizer } from '../rendering/BoundaryVisualizer';
import { HighscoreManager } from '../systems/HighscoreManager';
import { SaveStateManager } from '../systems/SaveStateManager';
import { CollectibleManager } from '../systems/CollectibleManager';
import { BestHeightManager } from '../systems/BestHeightManager';
import { UsernameManager } from '../systems/UsernameManager';
import { LeaderboardManager } from '../systems/LeaderboardManager';
import { SettingsManager } from '../systems/SettingsManager';
import { UIScene } from './UIScene';
import { VictoryManager, GameStats } from '../systems/VictoryManager';
import { FIXED_COLLECTIBLE_SPAWNS, validateCollectibleSpawns } from '../config/collectibleSpawns';
import { CollectibleTextureGenerator } from '../rendering/CollectibleTextureGenerator'; // PHASE 7A
import { DecorationTextureGenerator } from '../rendering/DecorationTextureGenerator'; // PHASE 7C

export class GameScene extends Phaser.Scene {
  private blobo!: Blobo;
  private platforms: Platform[] = [];
  private animatedPlatforms: Platform[] = []; // Performance: Track only platforms with animations (Zone 1)
  private collectibles: Collectible[] = []; // Collectible items
  private collectibleManager!: CollectibleManager;
  private tiltSystem!: TiltSystem;
  private devTools!: DevTools;
  private boundaryVisualizer!: BoundaryVisualizer;
  private currentCameraRotation: number = 0;
  private victoryManager?: VictoryManager;

  // Phase 5: Timer and highscore tracking
  private highscoreManager!: HighscoreManager;
  private timerStarted: boolean = false;
  private timerElapsed: number = 0; // milliseconds
  private currentHeight: number = 0; // Current height (goes down when falling)
  private maxHeight: number = 0; // Max height reached in this session
  private isNewRecord: boolean = false;
  private isPaused: boolean = false;
  private lastNotificationHeight: number = 0; // Last height where notification was shown
  private notificationHideTimer?: Phaser.Time.TimerEvent;
  private lastSubmittedBestHeight: number = 0; // Last height submitted to global leaderboard

  // MEMORY LEAK FIX (v0.8.6): Track if BLOBO_JUMP listener is registered to prevent accumulation
  private jumpListenerRegistered: boolean = false;

  // Phase 5: Victory stats tracking
  private gameStats: GameStats = {
    completionTime: 0,
    totalBounces: 0,
    totalFalls: 0,
    highestJump: 0,
    attempts: 0,
    collectibles: 0,
    totalCollectibles: 45,
    devMode: false,
  };

  // Phase 6: Dev mode tracking (teleport usage)
  private teleportUsed: boolean = false;

  // Performance: Throttle background and UI updates for better mobile performance
  private backgroundUpdateAccumulator: number = 0;
  private readonly BACKGROUND_UPDATE_INTERVAL: number = 100; // Update background every 100ms (~10fps)

  private uiUpdateAccumulator: number = 0;
  private readonly UI_UPDATE_INTERVAL: number = 66; // Update UI every 66ms (~15fps) - iOS performance fix

  // Performance: Platform culling (v0.3.6 - iOS optimization)
  private platformCullAccumulator: number = 0;
  private readonly PLATFORM_CULL_INTERVAL: number = 200; // Cull platforms every 200ms (~5fps)
  private readonly PLATFORM_CULL_MARGIN: number = 600; // PERFORMANCE: Reduced from 1000px to 600px (fewer platforms rendered)

  // Event handlers (store for proper cleanup to prevent memory leaks)
  private collisionHandler?: (event: any) => void;
  private heightChangeHandler?: (data: { currentHeight: number; maxHeight: number }) => void;
  private fallHandler?: (data: { fallDistance: number }) => void;
  private calibrateTiltHandler?: () => void;
  private teleportNextZoneHandler?: () => void;
  private returnToMenuHandler?: () => void;
  private resetHighscoreHandler?: () => void;
  private pauseGameHandler?: () => void;
  private resumeGameHandler?: () => void;
  private visibilityChangeHandler?: () => void;
  private windowBlurHandler?: () => void;
  private beforeUnloadHandler?: () => void;

  // Cleanup flag to prevent double-destroy
  private isCleanedUp: boolean = false;

  // Restart protection flag to prevent crash from multiple simultaneous restarts
  private isRestarting: boolean = false;

  // PERFORMANCE: Track previous gravity to avoid unnecessary updates
  private lastGravityX: number = 0;
  private lastGravityY: number = 1.2;

  // PERFORMANCE: Adaptive quality based on FPS monitoring
  private fpsHistory: number[] = [];
  private readonly FPS_HISTORY_SIZE: number = 60; // Track last 60 frames (~1 second at 60fps)
  private readonly FPS_LOW_THRESHOLD: number = 50; // Below 50 FPS, enable performance mode
  private readonly FPS_CRITICAL_THRESHOLD: number = 30; // Below 30 FPS, enable critical mode
  private readonly FPS_CHECK_INTERVAL: number = 1000; // Check FPS every 1 second
  private fpsCheckAccumulator: number = 0;
  private performanceModeEnabled: boolean = false;
  private criticalModeEnabled: boolean = false;

  // Input event handler storage for cleanup
  private pointerDownHandler?: () => void;
  private pointerUpHandler?: () => void;
  private escKey?: Phaser.Input.Keyboard.Key;
  private spaceKey?: Phaser.Input.Keyboard.Key;
  private rKey?: Phaser.Input.Keyboard.Key;

  // Performance diagnostics - Phase 1: Quick Diagnostic Tests (disabled for cleaner logs)
  // private perfFrameCount: number = 0;
  // private perfLastLog: number = 0;

  constructor() {
    super({ key: 'GameScene' });
  }

  create(): void {
    // PHASE 7A: Generate collectible textures ONCE at scene creation
    // This pre-generates all textures and caches them in GPU memory
    // Massive performance improvement over Graphics redrawing every frame
    CollectibleTextureGenerator.generateAllTextures(this);

    // PHASE 7C: Generate decoration textures ONCE at scene creation
    // Converts all platform decorations from Graphics to Sprites
    // Expected gain: 2-5 FPS on mobile devices (especially Firefox)
    DecorationTextureGenerator.generateAllTextures(this);

    // const { width, height } = this.cameras.main;

    // Reset flags (in case scene is restarted)
    this.isCleanedUp = false;
    this.isRestarting = false;
    this.isPaused = false;
    this.teleportUsed = false; // Reset dev mode flag

    // Clear skipAutoSave flag (set by reset/play again to prevent shutdown save)
    this.registry.set('skipAutoSave', false);
    // console.log(`Camera size: ${width}x${height}`);
    // console.log(`Camera zoom: ${this.cameras.main.zoom}`);
    // console.log(`🔄 Scene created/restarted - flags reset`);

    // Start background music
    SoundManager.startMusic();

    // Get selected level type from registry (default to 'main')
    const levelType = (this.registry.get('selectedLevel') as 'test' | 'main') || 'main';
    // console.log(`Loading level type: ${levelType}`);

    // Set world bounds based on level type
    let worldWidth: number;
    let worldHeight: number;

    if (levelType === 'test') {
      // Phase 3 test level: smaller world
      worldWidth = 800;
      worldHeight = 3000;
    } else {
      // Phase 5 main level: Extended to accommodate victory zone with proper top padding
      // Victory podium at y=11825 (level coords), needs ~900px clearance above for center visibility
      worldWidth = 800;
      worldHeight = 12725;
    }

    this.matter.world.setBounds(0, 0, worldWidth, worldHeight);

    // console.log(`World bounds: ${worldWidth}x${worldHeight}`);
    // console.log(`Gravity: ${GAME_CONSTANTS.PHYSICS.GRAVITY}`);

    // Load appropriate level
    if (levelType === 'test') {
      this.createTestLevel();
    } else {
      this.loadMainLevel();
    }

    // PARALLAX BACKGROUNDS - Segmented images for mobile compatibility
    console.log('🎨 Creating segmented parallax backgrounds (mobile-friendly)...');

    // Position images so bottoms align at world bottom
    // Each layer is split into segments to avoid mobile texture size limits (4096×4096)
    // Segments are stacked vertically with 2px overlap to prevent hairline gaps

    const cameraStartScrollY = 12700 - 844 / 2; // ≈ 11878
    const SEGMENT_OVERLAP = 2; // Pixels of overlap to prevent visible seams

    // Helper function to safely create background images with validation
    const createBackgroundSegment = (
      key: string,
      y: number,
      scrollFactor: number,
      depth: number
    ): Phaser.GameObjects.Image | null => {
      // Check if texture exists before creating image
      const texture = this.textures.get(key);
      if (!texture || texture.key === '__MISSING') {
        console.warn(`⚠️ Background segment ${key} not found - skipping`);
        return null;
      }

      const img = this.add.image(400, y, key);
      img.setOrigin(0.5, 1); // Bottom anchor
      img.setScrollFactor(1, scrollFactor);
      img.setDepth(depth);
      return img;
    };

    // FAR LAYER - 2 segments (2267px each) = 4534px total, scrollFactor=0.3
    const farBottomY = 844 + cameraStartScrollY * 0.3;
    const farSegments = [
      { key: 'bg-far-2', height: 2267 }, // Bottom segment
      { key: 'bg-far-1', height: 2267 }, // Top segment
    ];

    let currentY = farBottomY;
    let farCreated = 0;
    farSegments.forEach(segment => {
      const img = createBackgroundSegment(segment.key, currentY, 0.3, -3);
      if (img) {
        farCreated++;
        currentY -= segment.height - SEGMENT_OVERLAP; // Stack with overlap to hide seams
      }
    });
    if (farCreated > 0) {
      console.log(
        `  ✓ Far layer: ${farCreated}/${farSegments.length} segments created, scroll=0.3`
      );
    } else {
      console.warn(`  ⚠️ Far layer: No segments created (0/${farSegments.length})`);
    }

    // MID LAYER - 3 segments (2742px + 2741px + 2741px) = 8224px total, scrollFactor=0.6
    const midBottomY = 844 + cameraStartScrollY * 0.6;
    const midSegments = [
      { key: 'bg-mid-3', height: 2741 }, // Bottom segment
      { key: 'bg-mid-2', height: 2741 }, // Middle segment
      { key: 'bg-mid-1', height: 2742 }, // Top segment
    ];

    currentY = midBottomY;
    let midCreated = 0;
    midSegments.forEach(segment => {
      const img = createBackgroundSegment(segment.key, currentY, 0.6, -2);
      if (img) {
        midCreated++;
        currentY -= segment.height - SEGMENT_OVERLAP; // Stack with overlap to hide seams
      }
    });
    if (midCreated > 0) {
      console.log(
        `  ✓ Mid layer: ${midCreated}/${midSegments.length} segments created, scroll=0.6`
      );
    } else {
      console.warn(`  ⚠️ Mid layer: No segments created (0/${midSegments.length})`);
    }

    // NEAR LAYER - 3 segments (3767px + 3766px + 3766px) = 11299px total, scrollFactor=0.85
    const nearBottomY = 844 + cameraStartScrollY * 0.85;
    const nearSegments = [
      { key: 'bg-near-3', height: 3766 }, // Bottom segment
      { key: 'bg-near-2', height: 3766 }, // Middle segment
      { key: 'bg-near-1', height: 3767 }, // Top segment
    ];

    currentY = nearBottomY;
    let nearCreated = 0;
    nearSegments.forEach(segment => {
      const img = createBackgroundSegment(segment.key, currentY, 0.85, -1);
      if (img) {
        nearCreated++;
        currentY -= segment.height - SEGMENT_OVERLAP; // Stack with overlap to hide seams
      }
    });
    if (nearCreated > 0) {
      console.log(
        `  ✓ Near layer: ${nearCreated}/${nearSegments.length} segments created, scroll=0.85`
      );
    } else {
      console.warn(`  ⚠️ Near layer: No segments created (0/${nearSegments.length})`);
    }

    const totalCreated = farCreated + midCreated + nearCreated;
    if (totalCreated > 0) {
      console.log(`🎨 Segmented parallax backgrounds ready! (${totalCreated}/8 segments created)`);
    } else {
      console.error('🚨 NO background segments created! All backgrounds failed to load.');
    }

    // Initialize boundary visualizer for world edges
    this.boundaryVisualizer = new BoundaryVisualizer(this, worldHeight);

    // Create Blobo player - spawn position depends on level type
    let bloboSpawnX = worldWidth / 2;
    let bloboSpawnY: number;

    // Check if we should resume from saved state
    const savedState = SaveStateManager.loadState();
    const shouldResume = savedState && levelType !== 'test';

    if (shouldResume && savedState) {
      // Resume from saved position
      bloboSpawnX = savedState.bloboX;
      bloboSpawnY = savedState.bloboY;
      // console.log(`📂 Resuming from saved position: x=${bloboSpawnX}, y=${bloboSpawnY}`);
    } else if (levelType === 'test') {
      // Test level: spawn at top of floor (y=2950, height=40)
      // Top of platform at y=2930, Blobo radius 15, spawn at y=2915
      bloboSpawnY = 2915;
    } else {
      // Main level: spawn at top of starting platform (full-width ground)
      // Starting platform in level data: y=0 (bottom in climb-up coordinates)
      // After Y-flip: gameY = 12725 - 0 = 12725 (bottom of Phaser world)
      // Platform height=20, so top is at y=12725-10 = 12715
      // Blobo radius 15, spawn at y=12715-15 = 12700
      bloboSpawnY = 12700;
    }

    // console.log(`Blobo spawn: x=${bloboSpawnX}, y=${bloboSpawnY}`);
    this.blobo = new Blobo(this, bloboSpawnX, bloboSpawnY);

    // Phase 5: Initialize highscore manager and timer
    this.highscoreManager = new HighscoreManager();

    // Initialize collectible manager
    this.collectibleManager = new CollectibleManager(this);

    // Restore from saved state if available
    if (shouldResume && savedState) {
      this.timerStarted = true;
      this.timerElapsed = savedState.timerElapsed;
      this.currentHeight = savedState.currentHeight;
      this.maxHeight = savedState.maxHeight;

      // Restore the original ground level Y position for height tracking
      this.blobo.setStartY(savedState.bloboStartY);
      // Restore the highest point reached based on maxHeight
      this.blobo.setHighestYFromMaxHeight(savedState.maxHeight);

      // console.log(`📂 Restored timer: ${this.timerElapsed}ms, height: ${this.currentHeight}m, startY: ${savedState.bloboStartY}`);
    } else {
      this.timerStarted = false;
      this.timerElapsed = 0;
      this.currentHeight = 0;
      this.maxHeight = 0;
    }

    this.isNewRecord = false;
    this.lastNotificationHeight = 0;

    // Phase 5: Setup game event listeners for timer and highscore
    this.setupPhase5EventListeners();

    // Setup collision detection to track surface types
    // Store handler for cleanup (prevent memory leaks)
    this.collisionHandler = (event: any) => {
      event.pairs.forEach((pair: any) => {
        const { bodyA, bodyB } = pair;

        // Check if Blobo collided with a platform
        const bloboBody = this.blobo.getBody();
        if (bodyA === bloboBody || bodyB === bloboBody) {
          // Find which body is the platform
          const platformBody = bodyA === bloboBody ? bodyB : bodyA;

          // Find the platform object from our platforms array
          const platform = this.platforms.find(p => p.getBody() === platformBody);
          if (platform) {
            // Update Blobo's current surface type
            this.blobo.currentSurfaceType = platform.surfaceType;
          }
        }
      });
    };
    this.matter.world.on('collisionstart', this.collisionHandler);

    // NOTE: Collectible collision detection is set up in loadMainLevel() AFTER collectibles are spawned

    // Log actual Blobo position after creation
    // this.time.delayedCall(100, () => {
    //   const pos = this.blobo.getPosition();
    //   const body = this.blobo.getBody();
    //   console.log(`Blobo actual position: x=${pos.x.toFixed(1)}, y=${pos.y.toFixed(1)}`);
    //   console.log(`Blobo body position: x=${body.position.x.toFixed(1)}, y=${body.position.y.toFixed(1)}`);
    //   console.log(`Blobo radius from constants: ${GAME_CONSTANTS.BLOBO.RADIUS}`);
    // });

    // Initialize tilt system (Phase 2: Tilt Controls)
    this.tiltSystem = new TiltSystem(this);

    // Calibrate tilt to starting position
    // Small delay to allow gyroscope to stabilize
    this.time.delayedCall(100, () => {
      this.tiltSystem.calibrate();
    });

    // Initialize dev tools (only for main level with zones)
    if (levelType === 'main') {
      this.devTools = new DevTools(this, this.blobo, this.platforms);
      // console.log('[DevTools] Enabled - Use ⬆️ button to teleport between zones');
    }

    // Setup input handling
    this.setupInput();

    // Setup camera to follow Blobo
    this.setupCamera();

    // Phase 5: Initialize Victory Manager for main level only
    if (levelType === 'main') {
      this.victoryManager = new VictoryManager(this);
    }

    // Listen to UI events from UIScene (store handlers for cleanup)
    this.calibrateTiltHandler = () => {
      this.tiltSystem.calibrate();
    };
    this.events.on('calibrate-tilt', this.calibrateTiltHandler);

    this.teleportNextZoneHandler = () => {
      if (this.devTools) {
        this.devTools.teleportToNextZone();
        this.teleportUsed = true; // Mark as dev mode
        console.log('🚫 Dev mode activated - Run will not be saved to leaderboard');
      }
    };
    this.events.on('teleport-next-zone', this.teleportNextZoneHandler);

    this.returnToMenuHandler = () => {
      this.returnToMenu();
    };
    this.events.on('return-to-menu', this.returnToMenuHandler);

    // Stop music when scene shuts down (handles browser back navigation)
    this.events.once('shutdown', () => {
      // Save state before cleanup (unless it's a restart/reset scenario)
      // Check if blobo exists and we're not in the middle of a reset
      const skipAutoSave = this.registry.get('skipAutoSave') as boolean;
      if (this.blobo && !this.isRestarting && !skipAutoSave) {
        const bloboPos = this.blobo.getPosition();
        SaveStateManager.saveState({
          bloboX: bloboPos.x,
          bloboY: bloboPos.y,
          bloboStartY: this.blobo.getStartY(),
          timerElapsed: this.timerElapsed,
          currentHeight: this.currentHeight,
          maxHeight: this.maxHeight,
          timestamp: Date.now(),
        });
        console.log('💾 Auto-saved game state on shutdown');
      } else if (skipAutoSave) {
        console.log('🚫 Skipped auto-save (reset/restart in progress)');
      }
      this.cleanup();
    });

    // Start UIScene overlay
    this.scene.launch('UIScene');

    // Auto-pause when app loses focus (phone standby, app switch, etc.)
    this.setupAutoPause();

    // ESC key to return to menu (store for cleanup)
    this.escKey = this.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
    this.escKey?.on('down', () => {
      this.returnToMenu();
    });

    // Listen for reset highscore event (store handler for cleanup + add restart protection)
    this.resetHighscoreHandler = () => {
      console.log('[RESET] Handler called');
      // CRITICAL: Prevent multiple simultaneous restarts (causes crash)
      if (this.isRestarting) {
        console.warn('[RESET] Already restarting, ignoring');
        return;
      }
      this.isRestarting = true;
      console.log('[RESET] Setting isRestarting = true');

      this.highscoreManager.reset();
      SaveStateManager.clearState();
      this.collectibleManager.reset();
      console.log('[RESET] Highscore, saved game, and collectibles cleared');

      // CRITICAL FIX: Cleanup BEFORE restarting to prevent accumulation
      console.log('[RESET] Calling cleanup...');
      this.cleanup();

      // Restart the scene to apply changes
      console.log('[RESET] Calling scene.restart()');
      this.scene.restart();
      console.log('[RESET] scene.restart() completed');
    };
    this.events.on('reset-highscore', this.resetHighscoreHandler);

    // Debug: Show physics bodies (optional)
    // console.log(`SHOW_PHYSICS debug setting: ${GAME_CONSTANTS.DEBUG.SHOW_PHYSICS}`);
    if (GAME_CONSTANTS.DEBUG.SHOW_PHYSICS) {
      // console.log('Creating physics debug graphic...');
      const debugGraphic = this.matter.world.createDebugGraphic();
      // console.log('Debug graphic created:', debugGraphic);

      if (debugGraphic) {
        debugGraphic.setDepth(1000); // Make sure it's on top
        // console.log('Debug graphic depth set to 1000');
      }
    }
  }

  /**
   * Load main level from JSON (Phase 4: Fixed-level design)
   * Loads the pre-generated 10,000px climbing level
   */
  private async loadMainLevel(): Promise<void> {
    // console.log('=== LOADING MAIN LEVEL ===');

    try {
      // Fetch level data - service worker should cache this for offline use
      const response = await fetch('/levels/main-level.json', {
        cache: 'force-cache', // Try to use cached version first
      });

      if (!response.ok) {
        throw new Error(`Failed to load level: ${response.statusText}`);
      }

      const levelData = await response.json();
      // console.log(`Level loaded: ${levelData.metadata.platformCount} platforms`);
      // console.log(`Height: ${levelData.metadata.totalHeight}px`);
      // console.log(`Zones: ${levelData.metadata.zones.join(', ')}`);

      const WORLD_HEIGHT = 12725; // Match extended world height with victory zone

      // Create platforms from JSON data
      levelData.platforms.forEach((platformData: any, index: number) => {
        // Convert uppercase string surface type to enum
        const surfaceType = SurfaceType[platformData.surfaceType as keyof typeof SurfaceType];

        // CRITICAL: Flip Y-axis!
        // Level data uses Y=0 at bottom (climbing up), but Phaser uses Y=0 at top
        // Formula: gameY = WORLD_HEIGHT - levelY
        const gameY = WORLD_HEIGHT - platformData.y;

        const platform = new Platform(
          this,
          platformData.x,
          gameY,
          platformData.width,
          platformData.height,
          surfaceType,
          index // Platform ID for deterministic variations
        );

        // Apply angle if specified (for ANGLED surfaces)
        if (platformData.angle) {
          platform.setAngle(platformData.angle);
        }

        // Log first few platforms for debugging
        // if (index < 5) {
        //   console.log(`Platform ${index}: type=${platformData.surfaceType}, levelY=${platformData.y.toFixed(1)}, gameY=${gameY.toFixed(1)}, size=${platformData.width.toFixed(0)}x${platformData.height}`);
        // }

        this.platforms.push(platform);

        // Performance: Only Zone 1 (levelY 0-2000) has decorations, track those for animation updates
        // Zone 1: levelY 0-2000 = gameY 9500-11500 (since gameY = 11500 - levelY)
        if (platformData.y >= 0 && platformData.y <= 2000) {
          this.animatedPlatforms.push(platform);
        }
      });

      // console.log(`Total platforms created: ${this.platforms.length}`);

      // Spawn collectibles from platform decorations
      this.spawnCollectibles();

      // NOTE: Collectible collision detection now uses distance-based checks in update loop
      // No need for physics-based collision detection (massive performance improvement)

      // Log surface type distribution
      // const surfaceCount: Record<string, number> = {};
      // this.platforms.forEach(p => {
      //   const type = SurfaceType[p.surfaceType];
      //   surfaceCount[type] = (surfaceCount[type] || 0) + 1;
      // });
      // console.log('Surface distribution:', surfaceCount);
    } catch (error) {
      // Keep errors for critical debugging
      console.error('Failed to load main level:', error);

      // Show error message to user if offline or load fails
      const centerX = this.cameras.main.width / 2;
      const centerY = this.cameras.main.height / 2;

      const errorBg = this.add.graphics();
      errorBg.fillStyle(0x000000, 0.9);
      errorBg.fillRect(0, 0, this.cameras.main.width, this.cameras.main.height);
      errorBg.setScrollFactor(0);
      errorBg.setDepth(10000);

      const errorTitle = this.add.text(centerX, centerY - 80, '⚠️ Level Load Failed', {
        fontFamily: 'Arial',
        fontSize: '32px',
        color: '#FF6B6B',
        fontStyle: 'bold',
        resolution: 2,
      });
      errorTitle.setOrigin(0.5);
      errorTitle.setScrollFactor(0);
      errorTitle.setDepth(10001);

      const errorMessage = this.add.text(
        centerX,
        centerY - 20,
        navigator.onLine
          ? 'Failed to load game level.\nPlease refresh the page.'
          : "You're offline and level data\nisn't cached yet.\n\nPlease connect to the internet\nand try again.",
        {
          fontFamily: 'Arial',
          fontSize: '16px',
          color: '#FFFFFF',
          align: 'center',
          resolution: 2,
        }
      );
      errorMessage.setOrigin(0.5);
      errorMessage.setScrollFactor(0);
      errorMessage.setDepth(10001);

      // Add back to menu button
      const backButton = this.add.text(centerX, centerY + 80, '[BACK TO MENU]', {
        fontFamily: 'Arial',
        fontSize: '20px',
        color: '#4ECDC4',
        fontStyle: 'bold',
        resolution: 2,
      });
      backButton.setOrigin(0.5);
      backButton.setScrollFactor(0);
      backButton.setDepth(10001);
      backButton.setInteractive({ useHandCursor: true });
      backButton.on('pointerdown', () => {
        this.scene.start('MainMenuScene');
      });
      backButton.on('pointerover', () => backButton.setColor('#FFFFFF'));
      backButton.on('pointerout', () => backButton.setColor('#4ECDC4'));
    }
  }

  /**
   * Spawn collectibles using deterministic fixed positions
   * Always spawns exactly 45 collectibles at the same positions for fair, comparable runs
   */
  private spawnCollectibles(): void {
    // Validate configuration
    const validation = validateCollectibleSpawns();
    if (!validation.valid) {
      console.error('❌ Invalid collectible spawn configuration:', validation.errors);
      return;
    }

    console.log(`\n=== COLLECTIBLE SPAWN (Deterministic) ===`);
    console.log(
      `Spawning exactly ${FIXED_COLLECTIBLE_SPAWNS.length} collectibles at fixed positions`
    );
    console.log(
      `Already collected from previous session: ${this.collectibleManager.getCollected()}`
    );

    // Track per-zone statistics
    const zoneStats: Record<number, { collectibles: number }> = {
      1: { collectibles: 0 },
      2: { collectibles: 0 },
      3: { collectibles: 0 },
      4: { collectibles: 0 },
      5: { collectibles: 0 },
      6: { collectibles: 0 },
    };

    let spawned = 0;
    let alreadyCollected = 0;

    // Spawn each fixed collectible
    for (const spawn of FIXED_COLLECTIBLE_SPAWNS) {
      const platformIndex = spawn.platformIndex;
      const collectibleType = spawn.type;

      // Check if platform exists
      if (platformIndex >= this.platforms.length) {
        console.warn(
          `⚠️ Platform ${platformIndex} doesn't exist (only ${this.platforms.length} platforms)`
        );
        continue;
      }

      const platform = this.platforms[platformIndex];
      if (!platform) {
        console.warn(`⚠️ Platform ${platformIndex} is undefined`);
        continue;
      }

      const platformPos = platform.getPlatformPosition();
      const bounds = platform.getBounds();

      // Calculate collectible position on top of platform
      // Use platform center X, top Y (matching decoration positioning)
      const collectibleX = platformPos.x;
      let collectibleY = bounds.y - bounds.height / 2; // Platform top edge

      // Special positioning for FEATHER - needs to float higher above platform
      if (collectibleType === CollectibleType.FEATHER) {
        collectibleY -= 15; // Add upward offset for floating feathers
      }

      // Generate unique collectible ID
      const collectibleId = `p${platformIndex}_fixed_${collectibleType}`;

      // Always register with manager (even if already collected) to maintain correct total
      this.collectibleManager.register(collectibleId, collectibleType);

      // Track zone stats
      const zoneStatsEntry = zoneStats[spawn.zone];
      if (zoneStatsEntry) {
        zoneStatsEntry.collectibles++;
      }

      // Only spawn visual/physics object if not already collected
      if (!this.collectibleManager.isCollected(collectibleId)) {
        const collectible = new Collectible(
          this,
          collectibleX,
          collectibleY,
          collectibleType,
          platformIndex, // Use platformIndex as decorationIndex
          platformIndex, // Use platformIndex as platformId
          collectibleId // Pass the exact ID we're checking against
        );

        this.collectibles.push(collectible);
        spawned++;

        // Debug first few collectibles
        if (spawned <= 3) {
          console.log(
            `📍 Spawned ${collectible.type} at platform ${platformIndex} (${collectibleX.toFixed(0)}, ${collectibleY.toFixed(0)})`
          );
        }
      } else {
        alreadyCollected++;
      }
    }

    // Summary
    console.log(
      `Total: ${FIXED_COLLECTIBLE_SPAWNS.length} collectibles (${spawned} spawned, ${alreadyCollected} already collected)`
    );
    console.log(`Platforms: ${this.platforms.length} total\n`);
    console.log(`Zone Distribution:`);
    for (let zone = 1; zone <= 6; zone++) {
      const stats = zoneStats[zone];
      if (stats && stats.collectibles > 0) {
        console.log(`  Zone ${zone}: ${stats.collectibles} collectibles`);
      }
    }
    const spawnedTypes = new Set<string>();
    this.collectibles.forEach(c => spawnedTypes.add(c.type));
    console.log(
      `\n📋 Collectible types spawned (${spawnedTypes.size}): ${Array.from(spawnedTypes).sort().join(', ')}\n`
    );
  }

  /**
   * Handle collecting an item
   */
  private collectItem(collectible: Collectible): void {
    // Mark as collected in manager
    const wasCollected = this.collectibleManager.collect(collectible.id, collectible.type);

    if (wasCollected) {
      // Play collect animation
      collectible.collect();

      // TODO: Add sound effects for collectibles
      // For now, no sound (will be added in next phase with royalty-free sounds)
      // SoundManager.play(collectible.config.soundKey);

      // Haptic feedback - light tap for collection
      HapticManager.buttonTap();

      // Log collection (can be removed in production)
      const stats = this.collectibleManager.getStats();
      console.log(`🎯 Collected ${collectible.type}! (${stats.collected}/${stats.total})`);
    }
  }

  /**
   * Create Phase 3 test level with all surface types
   * Small level for testing and experimentation
   */
  private createTestLevel(): void {
    // console.log('=== CREATING PHASE 3 TEST LEVEL ===');

    const centerX = 400;
    let platformId = 0; // Counter for platform IDs

    // Create floor (normal surface)
    const floor = new Platform(this, centerX, 2950, 800, 40, SurfaceType.NORMAL, platformId++);
    this.platforms.push(floor);

    // Add label for floor
    this.add
      .text(centerX, 2900, 'NORMAL (Floor)', {
        fontSize: '16px',
        color: '#ffffff',
        fontFamily: TYPOGRAPHY.fonts.josefin,
      })
      .setOrigin(0.5);

    // Create platforms showcasing each surface type
    // Going from bottom to top

    // Level 1: STICKY (Green) - at ~2700
    const sticky = new Platform(this, centerX, 2700, 200, 20, SurfaceType.STICKY, platformId++);
    this.platforms.push(sticky);
    this.add
      .text(centerX, 2650, 'STICKY\n(High Friction)', {
        fontSize: '14px',
        color: '#00ff00',
        fontFamily: TYPOGRAPHY.fonts.josefin,
        align: 'center',
      })
      .setOrigin(0.5);

    // Level 2: SLIPPERY (Blue) - at ~2400
    const slippery = new Platform(this, centerX, 2400, 200, 20, SurfaceType.SLIPPERY, platformId++);
    this.platforms.push(slippery);
    this.add
      .text(centerX, 2350, 'SLIPPERY\n(No Friction)', {
        fontSize: '14px',
        color: '#00aaff',
        fontFamily: TYPOGRAPHY.fonts.josefin,
        align: 'center',
      })
      .setOrigin(0.5);

    // Level 3: BOUNCY (Orange/Red) - at ~2100
    const bouncy = new Platform(this, centerX, 2100, 200, 20, SurfaceType.BOUNCY, platformId++);
    this.platforms.push(bouncy);
    this.add
      .text(centerX, 2050, 'BOUNCY\n(High Restitution)', {
        fontSize: '14px',
        color: '#ff4400',
        fontFamily: TYPOGRAPHY.fonts.josefin,
        align: 'center',
      })
      .setOrigin(0.5);

    // Level 4: NORMAL (Gray) - at ~1800/1500
    const normal1 = new Platform(
      this,
      centerX - 100,
      1800,
      150,
      20,
      SurfaceType.NORMAL,
      platformId++
    );
    this.platforms.push(normal1);
    const normal2 = new Platform(
      this,
      centerX + 100,
      1500,
      150,
      20,
      SurfaceType.NORMAL,
      platformId++
    );
    this.platforms.push(normal2);
    this.add
      .text(centerX, 1750, 'NORMAL Platforms', {
        fontSize: '14px',
        color: '#808080',
        fontFamily: TYPOGRAPHY.fonts.josefin,
      })
      .setOrigin(0.5);

    // Level 5: ANGLED (Yellow) - at ~1300
    const angled = new Platform(this, centerX, 1300, 200, 20, SurfaceType.ANGLED, platformId++);
    this.platforms.push(angled);
    // Rotate the angled platform
    angled.setAngle(25);
    this.add
      .text(centerX, 1250, 'ANGLED\n(25° slope)', {
        fontSize: '14px',
        color: '#ffff00',
        fontFamily: TYPOGRAPHY.fonts.josefin,
        align: 'center',
      })
      .setOrigin(0.5);

    // Add some extra platforms for climbing higher
    const high1 = new Platform(
      this,
      centerX - 120,
      1000,
      120,
      20,
      SurfaceType.NORMAL,
      platformId++
    );
    this.platforms.push(high1);
    const high2 = new Platform(this, centerX + 120, 700, 120, 20, SurfaceType.STICKY, platformId++);
    this.platforms.push(high2);
    const high3 = new Platform(this, centerX, 400, 150, 20, SurfaceType.BOUNCY, platformId++);
    this.platforms.push(high3);
    const top = new Platform(this, centerX, 200, 200, 20, SurfaceType.NORMAL, platformId++);
    this.platforms.push(top);

    // Add "SUMMIT" text at the top
    this.add
      .text(centerX, 150, 'SUMMIT!', {
        fontSize: '24px',
        color: '#ffaa00',
        fontFamily: TYPOGRAPHY.fonts.josefin,
      })
      .setOrigin(0.5);

    // console.log(`Test level created: ${this.platforms.length} platforms`);
  }

  /**
   * Setup input handling for jump charging
   * CRITICAL: Store handler references for cleanup to prevent accumulation
   */
  private setupInput(): void {
    // Touch/mouse input for jump (store handlers for cleanup)
    this.pointerDownHandler = () => {
      this.blobo.startCharge();
    };
    this.input.on('pointerdown', this.pointerDownHandler);

    this.pointerUpHandler = () => {
      this.blobo.releaseCharge();
    };
    this.input.on('pointerup', this.pointerUpHandler);

    // Keyboard input for desktop testing (store keys for cleanup)
    this.spaceKey = this.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
    this.spaceKey?.on('down', () => {
      this.blobo.startCharge();
    });

    this.spaceKey?.on('up', () => {
      this.blobo.releaseCharge();
    });

    // R key to restart (for testing) - add restart protection
    this.rKey = this.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.R);
    this.rKey?.on('down', () => {
      if (!this.isRestarting) {
        this.isRestarting = true;
        // console.log('🧹 R key restart - calling cleanup first...');
        this.cleanup();
        this.scene.restart();
      }
    });
  }

  /**
   * Setup camera to follow Blobo
   */
  private setupCamera(): void {
    const camera = this.cameras.main;
    const { LERP_X, LERP_Y } = GAME_CONSTANTS.CAMERA;

    // Get level type to set appropriate camera bounds
    const levelType = (this.registry.get('selectedLevel') as 'test' | 'main') || 'main';
    const worldHeight = levelType === 'test' ? 3000 : 12725;

    // Set camera bounds to match world bounds
    camera.setBounds(0, 0, 800, worldHeight);

    // Follow Blobo's container (both horizontal and vertical)
    camera.startFollow(this.blobo.getContainer(), true, LERP_X, LERP_Y);

    // Set vertical offset to show more upward space
    // For portrait mobile, keep player in lower 40% of screen
    const offsetY = camera.height * 0.2; // Player at 60% from top
    camera.setFollowOffset(0, offsetY);

    // Set zoom
    camera.setZoom(GAME_CONSTANTS.CAMERA.ZOOM);
  }

  /**
   * Setup auto-pause when app loses focus
   * Triggers when phone goes to standby, user switches apps, etc.
   */
  private setupAutoPause(): void {
    // Page Visibility API - handles tab switching, phone lock, etc.
    this.visibilityChangeHandler = () => {
      if (document.hidden) {
        // App is hidden - auto-pause if not already paused
        if (!this.isPaused && !this.scene.isPaused('PauseScene')) {
          console.log('📱 App hidden - auto-pausing');
          this.game.events.emit(GAME_EVENTS.GAME_PAUSE);
        }
      }
    };
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);

    // Window blur - handles some edge cases not caught by visibility API
    this.windowBlurHandler = () => {
      if (!this.isPaused && !this.scene.isPaused('PauseScene')) {
        console.log('📱 Window blur - auto-pausing');
        this.game.events.emit(GAME_EVENTS.GAME_PAUSE);
      }
    };
    window.addEventListener('blur', this.windowBlurHandler);

    // Page unload - save state before page closes/refreshes
    this.beforeUnloadHandler = () => {
      if (this.blobo && !this.isRestarting) {
        const bloboPos = this.blobo.getPosition();
        const skipAutoSave = this.registry.get('skipAutoSave') as boolean;
        if (!skipAutoSave) {
          SaveStateManager.saveState({
            bloboX: bloboPos.x,
            bloboY: bloboPos.y,
            bloboStartY: this.blobo.getStartY(),
            timerElapsed: this.timerElapsed,
            currentHeight: this.currentHeight,
            maxHeight: this.maxHeight,
            timestamp: Date.now(),
          });
          console.log('💾 Game state saved on page unload');
        }
      }
    };
    window.addEventListener('beforeunload', this.beforeUnloadHandler);

    console.log('✅ Auto-pause listeners registered');

    // Log final physics state after scene creation
    if (this.matter?.world) {
      const totalBodies = this.matter.world.getAllBodies().length;
      console.log(`\n🎮 [SCENE CREATED] Total physics bodies: ${totalBodies}`);
      console.log(`   - Platforms: ${this.platforms.length}`);
      console.log(`   - Collectibles (visual): ${this.collectibles.length}`);
      console.log(`   - Blobo: 1\n`);
    }
  }

  /**
   * Setup Phase 5 event listeners for timer and highscore
   * MEMORY LEAK FIX (v0.8.6): Use flag to prevent duplicate BLOBO_JUMP listeners
   */
  private setupPhase5EventListeners(): void {
    // Listen for first jump to start timer (ONLY if not already started from resume)
    if (!this.timerStarted && !this.jumpListenerRegistered) {
      this.jumpListenerRegistered = true;
      this.events.once(GAME_EVENTS.BLOBO_JUMP, () => {
        this.jumpListenerRegistered = false; // Reset flag after listener fires
        this.startTimer();
      });
    }

    // Listen for victory event to stop timer
    this.events.on(GAME_EVENTS.VICTORY_ACHIEVED, () => {
      this.timerStarted = false; // Stop timer
      this.gameStats.completionTime = this.timerElapsed;
      // Haptic celebration feedback
      HapticManager.victory();
    });

    // Listen for height changes (store handler for cleanup)
    this.heightChangeHandler = (data: { currentHeight: number; maxHeight: number }) => {
      this.currentHeight = data.currentHeight;
      this.maxHeight = data.maxHeight;

      // Reset timer if back at ground level (height <= 0)
      if (this.currentHeight <= 0 && this.timerStarted) {
        this.timerElapsed = 0;
        this.timerStarted = false;
        // console.log('⏱️ Timer reset - back at ground level');

        // NOTE: Do NOT reset collectibles here - they persist across jumps
        // Collectibles only reset when explicitly resetting highscore

        // Re-register the timer start listener for next jump (MEMORY LEAK FIX: use flag)
        if (!this.jumpListenerRegistered) {
          this.jumpListenerRegistered = true;
          this.events.once(GAME_EVENTS.BLOBO_JUMP, () => {
            this.jumpListenerRegistered = false; // Reset flag after listener fires
            this.startTimer();
          });
        }
      }

      // Check if new highscore
      const wasNewRecord = this.isNewRecord;
      this.isNewRecord = this.highscoreManager.updateMaxHeight(this.maxHeight);

      // Track best height for leaderboard (only if not in dev mode)
      if (!this.teleportUsed && this.maxHeight >= 5) {
        // Only track if at least 5m high
        const username = UsernameManager.getUsername();
        BestHeightManager.updateBestHeight(username, this.maxHeight);
      }

      // Show notification every 25m and when breaking all-time record
      const heightDifference = Math.floor(this.maxHeight) - Math.floor(this.lastNotificationHeight);

      if (this.isNewRecord && !wasNewRecord) {
        // Just broke all-time highscore - always show
        this.showNewRecordNotification();
        this.lastNotificationHeight = Math.floor(this.maxHeight);
      } else if (heightDifference >= 25) {
        // Reached next 25m milestone
        this.showNewRecordNotification();
        this.lastNotificationHeight = Math.floor(this.maxHeight);
      }

      this.updateUIScene();
    };
    this.events.on(GAME_EVENTS.BLOBO_HEIGHT_CHANGE, this.heightChangeHandler);

    // Listen for fall events to play sad sound (store handler for cleanup)
    this.fallHandler = (data: { fallDistance: number }) => {
      const fallMeters = data.fallDistance / GAME_CONSTANTS.BLOBO.HEIGHT_SCALE;
      if (fallMeters > 5) {
        // Play OhNo sound for falls >5m (alternates between OhNo1 and OhNo2)
        SoundManager.playFallSound();
        // Haptic feedback for significant fall/death
        HapticManager.death();
        // console.log(`😢 Big fall: ${fallMeters.toFixed(1)}m`);
      }
    };
    this.events.on(GAME_EVENTS.BLOBO_FALL, this.fallHandler);

    // Listen for pause event (store handler for cleanup)
    this.pauseGameHandler = () => {
      this.pauseGame();
    };
    this.game.events.on(GAME_EVENTS.GAME_PAUSE, this.pauseGameHandler);

    // Listen for resume event (store handler for cleanup)
    this.resumeGameHandler = () => {
      this.resumeGame();
    };
    this.game.events.on(GAME_EVENTS.GAME_RESUME, this.resumeGameHandler);
  }

  /**
   * Pause the game
   */
  private async pauseGame(): Promise<void> {
    if (this.isPaused) return; // Already paused

    this.isPaused = true;

    // Pause Matter physics
    this.matter.pause();

    // CRITICAL: Save game state immediately when pausing
    // This ensures state survives if OS kills app from background
    if (this.blobo) {
      const bloboPos = this.blobo.getPosition();
      SaveStateManager.saveState({
        bloboX: bloboPos.x,
        bloboY: bloboPos.y,
        bloboStartY: this.blobo.getStartY(),
        timerElapsed: this.timerElapsed,
        currentHeight: this.currentHeight,
        maxHeight: this.maxHeight,
        timestamp: Date.now(),
      });
      console.log('💾 Game state saved on pause');
    }

    // Submit best height if it's a new personal best (and not using teleport)
    if (!this.teleportUsed && this.maxHeight >= 5) {
      // Only submit if current height is better than last submitted height
      if (this.maxHeight > this.lastSubmittedBestHeight) {
        const username = UsernameManager.getUsername();
        // Wait for submission to complete before showing pause screen
        await this.submitBestHeightToGlobal(username, this.maxHeight);
        this.lastSubmittedBestHeight = this.maxHeight; // Update tracking
        console.log(`📊 Best height submitted: ${this.maxHeight.toFixed(1)}m`);
      }
    }

    // Launch PauseScene (after submission completes)
    this.scene.launch('PauseScene');

    console.log('⏸️ Game paused');
  }

  /**
   * Resume the game
   */
  private resumeGame(): void {
    if (!this.isPaused) return; // Not paused

    this.isPaused = false;

    // Resume Matter physics
    this.matter.resume();

    console.log('▶️ Game resumed');
  }

  /**
   * Start the timer (called on first jump)
   */
  private startTimer(): void {
    this.timerStarted = true;
    this.timerElapsed = 0;
    this.events.emit(GAME_EVENTS.TIMER_START);
  }

  /**
   * Show new record notification (auto-hides after 2 seconds)
   */
  private showNewRecordNotification(): void {
    const uiScene = this.scene.get('UIScene') as UIScene;
    if (!uiScene) return;

    // Show the notification
    uiScene.showNewRecordNotification();

    // Cancel any existing hide timer
    if (this.notificationHideTimer) {
      this.notificationHideTimer.destroy();
    }

    // Auto-hide after 2 seconds
    this.notificationHideTimer = this.time.delayedCall(2000, () => {
      uiScene.hideNewRecordNotification();
      this.notificationHideTimer = undefined;
    });
  }

  /**
   * Submit best height to global leaderboard
   */
  private async submitBestHeightToGlobal(username: string, height: number): Promise<void> {
    // Check if leaderboard submission is disabled (developer setting)
    if (SettingsManager.getDisableLeaderboardSubmission()) {
      console.log('🚫 Leaderboard submission disabled (developer setting)');
      return;
    }

    try {
      const collectibleStats = this.collectibleManager?.getStats();
      const collectibles = collectibleStats?.collected || 0;
      const totalCollectibles = collectibleStats?.total || 45;
      console.log(
        `📤 Submitting best height to leaderboard: ${height.toFixed(1)}m with ${collectibles}/${totalCollectibles} collectibles`
      );

      const result = await LeaderboardManager.submitBestHeight(
        username,
        height,
        collectibles,
        totalCollectibles
      );

      if (result.success && result.isNewBest) {
        console.log(`✅ Best height rank: #${result.rank}`);
      } else if (result.success && !result.isNewBest) {
        console.log(`ℹ️ Not a new personal best`);
      }
    } catch (error) {
      console.error('❌ Error submitting best height:', error);
      // Gracefully fail - don't interrupt gameplay
    }
  }

  /**
   * Update UI scene with current game state (Phase 5)
   */
  private updateUIScene(): void {
    const uiScene = this.scene.get('UIScene') as UIScene;
    if (uiScene) {
      uiScene.updateHeight(this.currentHeight); // Show current height (not max)
      uiScene.updateTimer(this.timerElapsed);
      uiScene.updateHighscore(this.highscoreManager.getMaxHeight());
      uiScene.updateFPS(Math.round(this.game.loop.actualFps)); // Show actual FPS

      // Update collectibles counter
      const stats = this.collectibleManager.getStats();
      uiScene.updateCollectibles(stats.collected, stats.total);
    }
  }

  /**
   * Update debug information - send to UIScene (Currently unused - debug disabled)
   */
  // private updateDebugText(): void {
  //   const uiScene = this.scene.get('UIScene') as any;
  //   if (!uiScene || !uiScene.updateDebugText) return;

  //   const pos = this.blobo.getPosition();
  //   const body = this.blobo.getBody();
  //   const fps = Math.round(this.game.loop.actualFps);
  //   const tiltAngle = this.tiltSystem.getTiltAngle();
  //   const isGyro = this.tiltSystem.isUsingGyroscope();

  //   const debugInfo = [
  //     '--- DEBUG INFO ---',
  //     `FPS: ${fps}`,
  //     `Position: (${Math.round(pos.x)}, ${Math.round(pos.y)})`,
  //     `Velocity: (${body.velocity.x.toFixed(2)}, ${body.velocity.y.toFixed(2)})`,
  //     `Grounded: ${this.blobo.isGrounded}`,
  //     `Charging: ${this.blobo.isCharging}`,
  //     `Charge: ${(this.blobo.chargePercent * 100).toFixed(0)}%`,
  //     `Tilt: ${tiltAngle.toFixed(1)}° ${isGyro ? '(Gyro)' : '(Keys)'}`,
  //     '',
  //     'Controls:',
  //     'SPACE/TAP: Jump (hold to charge)',
  //     'A/D: Tilt left/right (desktop)',
  //     'Mobile: Tilt side edges forward/back',
  //     'R: Restart',
  //     'ESC/Menu: Return to menu',
  //   ];

  //   uiScene.updateDebugText(debugInfo);
  // }

  update(time: number, delta: number): void {
    // CRITICAL: Cap delta time to prevent physics tunneling
    // When FPS drops, this prevents Blobo from moving too far in one frame
    // 33ms = ~30 FPS minimum, prevents falling through platforms
    delta = Math.min(delta, 33);

    // PERFORMANCE DIAGNOSTICS - Phase 1: Quick Diagnostic Tests
    // Reduced logging: Performance stats disabled during gameplay
    // Uncomment below to enable detailed FPS/object count logging
    /*
    this.perfFrameCount++;
    if (time - this.perfLastLog > 1000) {
      const fps = Math.round(this.game.loop.actualFps);
      const platformCount = this.platforms.length;
      const animatedCount = this.animatedPlatforms.length;
      const bodyCount = this.matter.world.getAllBodies().length;
      const displayCount = this.children.length;
      const rendererType = this.game.renderer.type === Phaser.WEBGL ? 'WebGL' : 'Canvas';

      // PHASE 6: Count total animated decorations across visible platforms
      let totalAnimatedDecorations = 0;
      const camera = this.cameras.main;
      const viewportTop = camera.scrollY - this.PLATFORM_CULL_MARGIN;
      const viewportBottom = camera.scrollY + camera.height + this.PLATFORM_CULL_MARGIN;

      for (const platform of this.animatedPlatforms) {
        const bounds = platform.getBounds();
        const platformTop = bounds.y - bounds.height / 2;
        const platformBottom = bounds.y + bounds.height / 2;
        const isVisible = !(platformBottom < viewportTop || platformTop > viewportBottom);

        if (isVisible) {
          // Access the renderer's decorator to count animated elements
          const renderer = (platform as any).renderer;
          if (renderer) {
            const decorator = renderer.getDecorator();
            if (decorator) {
              totalAnimatedDecorations += decorator.getAnimatedElements().length;
            }
          }
        }
      }

      console.log(`📊 FPS: ${fps} | Platforms: ${platformCount} | Animated: ${animatedCount} | AnimatedDecorations: ${totalAnimatedDecorations} | Bodies: ${bodyCount} | Display: ${displayCount} | Renderer: ${rendererType} | Frames: ${this.perfFrameCount}`);
      this.perfLastLog = time;
      this.perfFrameCount = 0;
    }
    */

    // Update tilt system (Phase 2: Tilt Controls)
    this.tiltSystem.update(delta);

    // Apply tilt rotation to world (camera rotation)
    const tiltRadians = this.tiltSystem.getTiltRadians();
    const camera = this.cameras.main;

    // Smooth camera rotation
    this.currentCameraRotation = Phaser.Math.Linear(
      this.currentCameraRotation,
      tiltRadians,
      GAME_CONSTANTS.CAMERA.ROTATION_SMOOTHING
    );
    camera.setRotation(this.currentCameraRotation);

    // PERFORMANCE: Only update gravity when tilt changes significantly
    // Gravity always points "down" relative to the tilted world
    const gravityMagnitude = GAME_CONSTANTS.PHYSICS.GRAVITY;
    const newGravityX = Math.sin(tiltRadians) * gravityMagnitude;
    const newGravityY = Math.cos(tiltRadians) * gravityMagnitude;

    // Only set gravity if it changed by more than 0.01 (avoids unnecessary physics updates)
    const gravityThreshold = 0.01;
    if (
      Math.abs(newGravityX - this.lastGravityX) > gravityThreshold ||
      Math.abs(newGravityY - this.lastGravityY) > gravityThreshold
    ) {
      this.matter.world.setGravity(newGravityX, newGravityY);
      this.lastGravityX = newGravityX;
      this.lastGravityY = newGravityY;
    }

    // Update Blobo
    this.blobo.update(time, delta);

    // Update collectibles - simple distance-based approach
    // Only process collectibles near Blobo (much simpler and faster than camera bounds)
    const bloboBody = this.blobo.getBody();
    const bloboX = bloboBody.position.x;
    const bloboY = bloboBody.position.y;

    // Single distance check for both animation and collection
    const updateRadius = 300; // Update animations within 300px of Blobo
    const updateRadiusSq = updateRadius * updateRadius;
    const collectRadius = 45; // 25 (collectible) + 20 (Blobo)
    const collectRadiusSq = collectRadius * collectRadius;

    for (let i = 0; i < this.collectibles.length; i++) {
      const collectible = this.collectibles[i];
      if (!collectible) continue;

      const pos = collectible.getPosition();
      const dx = pos.x - bloboX;
      const dy = pos.y - bloboY;
      const distSq = dx * dx + dy * dy;

      // Only update if within 300px radius
      if (distSq < updateRadiusSq) {
        collectible.update(delta);

        // Check for collection (tighter radius)
        if (!collectible.isAlreadyCollected() && distSq < collectRadiusSq) {
          this.collectItem(collectible);
        }
      }
    }

    // Phase 5: Update victory manager if present
    if (this.victoryManager) {
      const bloboBody = this.blobo.getBody();
      this.victoryManager.update({
        x: bloboBody.position.x,
        y: bloboBody.position.y,
      });
    }

    // Phase 5: Update timer if started and not paused (using ACTUAL delta time)
    if (this.timerStarted && !this.isPaused) {
      this.timerElapsed += delta;
    }

    // Performance: Throttle UI updates to ~15fps (iOS performance fix - text rendering is expensive)
    this.uiUpdateAccumulator += delta;
    if (this.uiUpdateAccumulator >= this.UI_UPDATE_INTERVAL) {
      this.updateUIScene();
      this.uiUpdateAccumulator -= this.UI_UPDATE_INTERVAL;
    }

    // Performance: Throttle background updates to ~10fps (color interpolation is expensive)
    // Skip if backgrounds disabled OR in critical performance mode
    if (!GAME_CONSTANTS.DEBUG.DISABLE_BACKGROUNDS && !this.criticalModeEnabled) {
      this.backgroundUpdateAccumulator += delta;
      if (this.backgroundUpdateAccumulator >= this.BACKGROUND_UPDATE_INTERVAL) {
        if (this.boundaryVisualizer) {
          this.boundaryVisualizer.update(this.cameras.main.scrollY);
        }
        this.backgroundUpdateAccumulator -= this.BACKGROUND_UPDATE_INTERVAL;
      }
    }

    // PERFORMANCE: Adaptive quality - monitor FPS and auto-enable performance mode
    this.monitorPerformance(delta);

    // Performance: Platform culling (v0.3.6 - iOS optimization)
    // Cull platforms outside viewport to reduce render overhead
    // Typical savings: 400 platforms → 20-40 visible (90-95% reduction)
    this.platformCullAccumulator += delta;
    if (this.platformCullAccumulator >= this.PLATFORM_CULL_INTERVAL) {
      this.cullPlatforms();
      this.platformCullAccumulator -= this.PLATFORM_CULL_INTERVAL;
    }

    // Update animated platforms (Zone 1 decorations)
    // PHASE 3 OPTIMIZATION: Only update animations for VISIBLE platforms (massive FPS improvement)
    // Skip if disabled for testing OR in critical performance mode
    if (!GAME_CONSTANTS.DEBUG.DISABLE_ANIMATED_PLATFORMS && !this.criticalModeEnabled) {
      // Calculate viewport bounds for visibility check
      const camera = this.cameras.main;
      const viewportTop = camera.scrollY - this.PLATFORM_CULL_MARGIN;
      const viewportBottom = camera.scrollY + camera.height + this.PLATFORM_CULL_MARGIN;

      // Only update platforms that are visible in viewport
      for (const platform of this.animatedPlatforms) {
        const bounds = platform.getBounds();
        const platformTop = bounds.y - bounds.height / 2;
        const platformBottom = bounds.y + bounds.height / 2;
        const isVisible = !(platformBottom < viewportTop || platformTop > viewportBottom);

        // Only update animations for visible platforms
        if (isVisible) {
          platform.update(delta);
        }
      }
    }
  }

  /**
   * Cull platforms outside viewport (v0.8.4 - iOS performance optimization + physics culling)
   * Only renders platforms visible in camera + margin
   * Also disables physics for off-screen platforms to reduce collision detection overhead
   * Typical savings: ~105 platforms total → 20-40 visible (80-90% reduction in render + physics)
   */
  private cullPlatforms(): void {
    const camera = this.cameras.main;

    // Calculate viewport bounds with cull margin
    const viewportTop = camera.scrollY - this.PLATFORM_CULL_MARGIN;
    const viewportBottom = camera.scrollY + camera.height + this.PLATFORM_CULL_MARGIN;

    // Cull each platform based on Y position
    for (const platform of this.platforms) {
      const bounds = platform.getBounds();

      // Check if platform intersects with viewport
      const platformTop = bounds.y - bounds.height / 2;
      const platformBottom = bounds.y + bounds.height / 2;
      const isVisible = !(platformBottom < viewportTop || platformTop > viewportBottom);

      // Toggle visibility and physics (v0.8.4 - physics culling optimization)
      platform.setVisible(isVisible);
      platform.setPhysicsActive(isVisible);
    }
  }

  /**
   * PERFORMANCE: Monitor FPS and automatically enable performance mode if needed
   * Tracks average FPS over 1 second and reduces visual effects if below threshold
   */
  private monitorPerformance(delta: number): void {
    // Track current FPS
    const currentFPS = this.game.loop.actualFps;
    this.fpsHistory.push(currentFPS);

    // Keep history size limited
    if (this.fpsHistory.length > this.FPS_HISTORY_SIZE) {
      this.fpsHistory.shift();
    }

    // Check FPS periodically (every 1 second)
    this.fpsCheckAccumulator += delta;
    if (this.fpsCheckAccumulator >= this.FPS_CHECK_INTERVAL) {
      this.fpsCheckAccumulator = 0;

      // Calculate average FPS
      if (this.fpsHistory.length > 0) {
        const avgFPS = this.fpsHistory.reduce((a, b) => a + b, 0) / this.fpsHistory.length;

        // Enable critical mode for very low FPS devices (e.g., old tablets)
        if (avgFPS < this.FPS_CRITICAL_THRESHOLD && !this.criticalModeEnabled) {
          console.warn(
            `🔴 Critical FPS detected (${avgFPS.toFixed(1)} FPS) - enabling critical performance mode`
          );
          this.enableCriticalMode();
        }
        // Enable performance mode if FPS is consistently low
        else if (avgFPS < this.FPS_LOW_THRESHOLD && !this.performanceModeEnabled) {
          console.warn(
            `⚠️ Low FPS detected (${avgFPS.toFixed(1)} FPS) - enabling performance mode`
          );
          this.enablePerformanceMode();
        }
      }
    }
  }

  /**
   * PERFORMANCE: Enable performance mode - reduces visual effects for better frame rate
   * This is automatically triggered when FPS drops below 50
   */
  private enablePerformanceMode(): void {
    if (this.performanceModeEnabled) return;

    this.performanceModeEnabled = true;
    console.log('🚀 Performance mode enabled - reducing visual effects');

    // Reduce UI update frequency (from 15fps to 10fps)
    (this as any).UI_UPDATE_INTERVAL = 100; // 10fps

    // Reduce platform culling checks (from 5fps to 3fps)
    (this as any).PLATFORM_CULL_INTERVAL = 333; // ~3fps

    // Reduce background update frequency (from 10fps to 5fps)
    (this as any).BACKGROUND_UPDATE_INTERVAL = 200; // 5fps

    // Reduce collectible update radius (from 300px to 200px)
    // This means fewer collectibles get their animations updated
    console.log('  → Reduced update frequencies and render distances');
  }

  /**
   * PERFORMANCE: Enable critical mode for very low-end devices
   * This is automatically triggered when FPS drops below 30
   */
  private enableCriticalMode(): void {
    if (this.criticalModeEnabled) return;

    // First enable standard performance mode
    if (!this.performanceModeEnabled) {
      this.enablePerformanceMode();
    }

    this.criticalModeEnabled = true;
    console.log('🔴 CRITICAL performance mode enabled - maximum optimizations');

    // Ultra-aggressive optimizations for old devices
    (this as any).UI_UPDATE_INTERVAL = 200; // 5fps
    (this as any).BACKGROUND_UPDATE_INTERVAL = 500; // 2fps
    (this as any).PLATFORM_CULL_INTERVAL = 500; // 2fps
    (this as any).PLATFORM_CULL_MARGIN = 400; // Render even fewer platforms (was 600px)

    // Note: Background updates are automatically skipped in critical mode
    // via the criticalModeEnabled flag check in the update loop
    console.log('  → Ultra-low update frequencies, minimal rendering');
  }

  /**
   * Cleanup all resources (called by shutdown or returnToMenu)
   * CRITICAL: Prevents double-destroy crash and resource accumulation
   */
  private cleanup(): void {
    console.log('[CLEANUP] Starting cleanup...');

    // Log physics state before cleanup
    if (this.matter?.world) {
      const bodiesBeforeCleanup = this.matter.world.getAllBodies().length;
      console.log(`[CLEANUP] Physics bodies before cleanup: ${bodiesBeforeCleanup}`);
    }

    // Prevent double-cleanup (happens when returnToMenu calls scene.start which triggers shutdown)
    if (this.isCleanedUp) {
      console.log('[CLEANUP] Already cleaned up, skipping');
      return;
    }
    this.isCleanedUp = true;

    // Flush any pending collectible saves
    if (this.collectibleManager) {
      this.collectibleManager.flushPendingSave();
    }

    console.log('[CLEANUP] Destroying resources...');

    // Stop music
    SoundManager.stopMusic();
    console.log('[CLEANUP] Music stopped');

    // CRITICAL: Destroy all platforms and blobo FIRST (before removing listeners)
    // This was missing in restart flow, causing platforms to accumulate!
    if (this.platforms && this.platforms.length > 0) {
      // console.log(`🧹 Destroying ${this.platforms.length} platforms...`);
      this.platforms.forEach(platform => platform.destroy());
      this.platforms = [];
      this.animatedPlatforms = [];
    }

    // Destroy all collectibles
    if (this.collectibles && this.collectibles.length > 0) {
      // console.log(`🧹 Destroying ${this.collectibles.length} collectibles...`);
      this.collectibles.forEach(collectible => {
        if (collectible) {
          collectible.destroy();
        }
      });
      this.collectibles = [];
    }

    if (this.blobo) {
      this.blobo.destroy();
      // console.log('🧹 Destroyed Blobo');
    }

    // CRITICAL: Remove all event listeners to prevent memory leaks & accumulation
    if (this.collisionHandler && this.matter?.world) {
      this.matter.world.off('collisionstart', this.collisionHandler);
      // console.log('🧹 Removed Matter.js collision listener');
    }
    if (this.heightChangeHandler) {
      this.events.off(GAME_EVENTS.BLOBO_HEIGHT_CHANGE, this.heightChangeHandler);
      // console.log('🧹 Removed height change listener');
    }
    if (this.fallHandler) {
      this.events.off(GAME_EVENTS.BLOBO_FALL, this.fallHandler);
      // console.log('🧹 Removed fall listener');
    }
    if (this.calibrateTiltHandler) {
      this.events.off('calibrate-tilt', this.calibrateTiltHandler);
      // console.log('🧹 Removed calibrate-tilt listener');
    }
    if (this.teleportNextZoneHandler) {
      this.events.off('teleport-next-zone', this.teleportNextZoneHandler);
      // console.log('🧹 Removed teleport-next-zone listener');
    }
    if (this.returnToMenuHandler) {
      this.events.off('return-to-menu', this.returnToMenuHandler);
      // console.log('🧹 Removed return-to-menu listener');
    }
    if (this.resetHighscoreHandler) {
      this.events.off('reset-highscore', this.resetHighscoreHandler);
      // console.log('🧹 Removed reset-highscore listener');
    }

    // CRITICAL: Remove game-level pause/resume listeners
    if (this.pauseGameHandler) {
      this.game.events.off(GAME_EVENTS.GAME_PAUSE, this.pauseGameHandler);
      // console.log('🧹 Removed pause game listener');
    }
    if (this.resumeGameHandler) {
      this.game.events.off(GAME_EVENTS.GAME_RESUME, this.resumeGameHandler);
      // console.log('🧹 Removed resume game listener');
    }

    // CRITICAL: Remove input event listeners to prevent accumulation
    if (this.pointerDownHandler) {
      this.input.off('pointerdown', this.pointerDownHandler);
      // console.log('🧹 Removed pointerdown listener');
    }
    if (this.pointerUpHandler) {
      this.input.off('pointerup', this.pointerUpHandler);
      // console.log('🧹 Removed pointerup listener');
    }

    // Remove keyboard event listeners
    if (this.escKey) {
      this.escKey.removeAllListeners();
      // console.log('🧹 Removed ESC key listeners');
    }
    if (this.spaceKey) {
      this.spaceKey.removeAllListeners();
      // console.log('🧹 Removed SPACE key listeners');
    }
    if (this.rKey) {
      this.rKey.removeAllListeners();
      // console.log('🧹 Removed R key listeners');
    }

    // Remove auto-pause listeners (window/document events)
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
      // console.log('🧹 Removed visibilitychange listener');
    }
    if (this.windowBlurHandler) {
      window.removeEventListener('blur', this.windowBlurHandler);
      // console.log('🧹 Removed window blur listener');
    }
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      // console.log('🧹 Removed beforeunload listener');
    }

    // Destroy all systems
    if (this.tiltSystem) {
      this.tiltSystem.destroy();
      // console.log('🧹 Destroyed TiltSystem');
    }
    if (!GAME_CONSTANTS.DEBUG.DISABLE_BACKGROUNDS) {
      if (this.boundaryVisualizer) {
        this.boundaryVisualizer.destroy();
        // console.log('🧹 Destroyed BoundaryVisualizer');
      }
    }
    if (this.victoryManager) {
      console.log('[CLEANUP] Destroying VictoryManager...');
      this.victoryManager.destroy();
      console.log('[CLEANUP] VictoryManager destroyed');
    }

    // Log physics state after cleanup
    if (this.matter?.world) {
      const bodiesAfterCleanup = this.matter.world.getAllBodies().length;
      console.log(`[CLEANUP] Physics bodies after cleanup: ${bodiesAfterCleanup}`);
    }

    console.log('[CLEANUP] ✅ Cleanup complete');
  }

  /**
   * Get elapsed time for victory stats
   */
  public getElapsedTime(): number {
    return this.timerElapsed;
  }

  /**
   * Get game statistics for victory screen
   */
  public getGameStats(): GameStats {
    // Update stats with current values
    this.gameStats.completionTime = this.timerElapsed;
    this.gameStats.highestJump = this.maxHeight;

    // Load attempts from localStorage
    const attempts = parseInt(localStorage.getItem('blobo-attempts') || '0') + 1;
    localStorage.setItem('blobo-attempts', attempts.toString());
    this.gameStats.attempts = attempts;

    // Get collectibles count
    const collectibleStats = this.collectibleManager?.getStats();
    this.gameStats.collectibles = collectibleStats?.collected || 0;
    this.gameStats.totalCollectibles = collectibleStats?.total || 45;

    // Include dev mode flag (teleport usage)
    this.gameStats.devMode = this.teleportUsed;

    return this.gameStats;
  }

  /**
   * Return to main menu
   */
  private returnToMenu(): void {
    console.log('[MENU] returnToMenu() called');

    if (!this.blobo) {
      console.error('[MENU] ERROR: blobo is undefined!');
      return;
    }

    // Save current game state for resume (BEFORE cleanup destroys blobo!)
    const bloboPos = this.blobo.getPosition();
    console.log('[MENU] Blobo position:', bloboPos);

    SaveStateManager.saveState({
      bloboX: bloboPos.x,
      bloboY: bloboPos.y,
      bloboStartY: this.blobo.getStartY(), // Save original ground level for height tracking
      timerElapsed: this.timerElapsed,
      currentHeight: this.currentHeight,
      maxHeight: this.maxHeight,
      timestamp: Date.now(),
    });
    console.log('[MENU] State saved');

    // Perform cleanup (destroys platforms, blobo, listeners, systems)
    // Will be called again by shutdown, but protected by isCleanedUp flag
    console.log('[MENU] Calling cleanup...');
    this.cleanup();

    // Stop UI scene
    console.log('[MENU] Stopping UIScene');
    this.scene.stop('UIScene');

    // Return to main menu (this triggers 'shutdown' event, but cleanup is protected)
    console.log('[MENU] Starting MainMenuScene');
    this.scene.start('MainMenuScene');
    console.log('[MENU] MainMenuScene started');
  }
}
